import React, { useCallback, useContext, useState } from 'react'
import { get, post } from 'aws-amplify/api'
import HomeContext from '../context/HomeContext'
import HeaderContext from '../context/HeaderContext'
import { useAuth0 } from '@auth0/auth0-react'
import getQueryParam from '../utils/getQueryParam'
import { buildPathWithQuery } from '../utils/buildQueryString'
import errorTrackingService from '../services/errorTrackingService'

const HomeProvider = ({ children }) => {
  const { setFum } = useContext(HeaderContext)
  const { getIdTokenClaims } = useAuth0()
  const [respGetBabyWeeklyGrowth, setRespgGetBabyWeeklyGrowth] = useState(null)

  const baseUrl = process.env.REACT_APP_API_BASE_URL

  const getBabyWeeklyGrowth = async (startDate) => {
    const idToken = await getIdTokenClaims()
    if (idToken !== '') {
      try {
        const resp = get({
          apiName: baseUrl,
          path: `/api/v2/pregnancies/weekly-growth?startDate=${startDate.toISOString()}`,
          options: {
            headers: {
              'content-type': 'application/json',
              Authorization: `Bearer ${idToken.__raw}`
            }
          }
        })
        const { body } = await resp.response
        const data = await body.json()

        if (data.success === false) {
          const error = new Error(data.message || 'API returned success: false')
          error.isAPIError = true
          error.response = { status: 200, data }
          errorTrackingService.trackAPIError(error, '/api/v2/pregnancies/weekly-growth', 'GET')
          return undefined
        }

        setRespgGetBabyWeeklyGrowth(data)
        setFum(data && data.data ? data.data.lastMenstruationDate : '')
        return data.data
      } catch (error) {
        errorTrackingService.trackAPIError(error, '/api/v2/pregnancies/weekly-growth', 'GET')
        console.log(error)
        return undefined
      }
    }
  }

  const getHomeData = async () => {
    const idToken = await getIdTokenClaims()
    if (!idToken) return null

    try {
      const resp = get({
        apiName: baseUrl,
        path: '/api/v2/patients/home',
        options: {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${idToken.__raw}`
          }
        }
      })
      const { body } = await resp.response
      const data = await body.json()

      if (data.success === false) {
        const error = new Error(data.message || 'API returned success: false')
        error.isAPIError = true
        error.response = { status: 200, data }
        errorTrackingService.trackAPIError(error, '/api/v2/patients/home', 'GET')
        return undefined
      }

      return data.data
    } catch (error) {
      errorTrackingService.trackAPIError(error, '/api/v2/patients/home', 'GET')
      console.log(error)
      return undefined
    }
  }

  const getDetailWeek = useCallback(async (token, week) => {
    if (week === '' || token == null) return undefined

    try {
      const resp = get({
        apiName: baseUrl,
        path: `/health/weeklyDetailBabyGrowth?week=${week}`,
        options: {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${token.__raw}`
          }
        }
      })
      const { body } = await resp.response
      const data = await body.json()

      if (data.success === false) {
        const error = new Error(data.message || 'API returned success: false')
        error.isAPIError = true
        error.response = { status: 200, data }
        errorTrackingService.trackAPIError(error, '/health/weeklyDetailBabyGrowth', 'GET')
        return undefined
      }

      return data.data
    } catch (error) {
      errorTrackingService.trackAPIError(error, '/health/weeklyDetailBabyGrowth', 'GET')
      console.log(error)
      return undefined
    }
  }, [baseUrl])

  const updateGrief = async (token) => {
    try {
      const resp = post({
        apiName: baseUrl,
        path: '/auth/grief',
        options: {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${token.__raw}`
          },
          body: {
            grief: false
          }
        }
      })
      const { body } = await resp.response
      const data = await body.json()

      if (data.success === false) {
        const error = new Error(data.message || 'API returned success: false')
        error.isAPIError = true
        error.response = { status: 200, data }
        errorTrackingService.trackAPIError(error, '/auth/grief', 'POST')
        return undefined
      }

      return data.data
    } catch (error) {
      errorTrackingService.trackAPIError(error, '/auth/grief', 'POST')
      console.log(error)
      return undefined
    }
  }

  const getDataTimeline = async (timelineId) => {
    const idToken = await getIdTokenClaims()
    try {
      const queryParams = new URLSearchParams()
      queryParams.append('timeline_id', timelineId)
      queryParams.append('limit', '3')
      queryParams.append('done', 'false')

      const pathWithParams = `/api/v3/timelines?${queryParams.toString()}`
      const resp = get({
        apiName: baseUrl,
        path: pathWithParams,
        options: {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${idToken.__raw}`
          }
        }
      })
      const { body } = await resp.response
      const data = await body.json()

      if (data.success === false) {
        const error = new Error(data.message || 'API returned success: false')
        error.isAPIError = true
        error.response = { status: 200, data }
        errorTrackingService.trackAPIError(error, '/api/v3/timelines', 'GET')
        return undefined
      }

      return data
    } catch (error) {
      errorTrackingService.trackAPIError(error, '/api/v3/timelines', 'GET')
      console.log('HomeProvider getDataTimeline error:', error)
      return undefined
    }
  }

  const getSubscriptionsCorporate = async () => {
    const idToken = await getIdTokenClaims()
    const brand = getQueryParam('brand')
    const businessType = getQueryParam('business_type')
    const uniqueCode = getQueryParam('uniqueCode' || 'unique_code')

    try {
      const pathWithParams = buildPathWithQuery('/api/v2/subscriptions/corporate', {
        brand: brand || 'amazon',
        business_type: businessType || 'services',
        unique_code: uniqueCode
      })

      const resp = get({
        apiName: baseUrl,
        path: pathWithParams,
        options: {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${idToken.__raw}`
          }
        }
      })
      const { body } = await resp.response
      const data = await body.json()

      if (data.success === false) {
        const error = new Error(data.message || 'API returned success: false')
        error.isAPIError = true
        error.response = { status: 200, data }
        errorTrackingService.trackAPIError(error, '/api/v2/subscriptions/corporate', 'GET')
        return undefined
      }

      return data.data
    } catch (error) {
      errorTrackingService.trackAPIError(error, '/api/v2/subscriptions/corporate', 'GET')
      console.log(error)
      return undefined
    }
  }

  const postSubscriptionsCorporate = async () => {
    const idToken = await getIdTokenClaims()
    const brand = getQueryParam('brand')
    const businessType = getQueryParam('business_type')
    const uniqueCode = getQueryParam('uniqueCode' || 'unique_code')

    try {
      const resp = post({
        apiName: baseUrl,
        path: '/api/v2/subscriptions/corporate',
        options: {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${idToken.__raw}`
          },
          body: {
            brand: brand || 'amazon',
            businessType: businessType || 'services',
            uniqueCode
          }
        }
      })
      const { body } = await resp.response
      const data = await body.json()

      if (data.success === false) {
        const error = new Error(data.message || 'API returned success: false')
        error.isAPIError = true
        error.response = { status: 200, data }
        errorTrackingService.trackAPIError(error, '/api/v2/subscriptions/corporate', 'POST')
        return undefined
      }

      return data.data
    } catch (error) {
      errorTrackingService.trackAPIError(error, '/api/v2/subscriptions/corporate', 'POST')
      console.log(error)
      return undefined
    }
  }

  return (
    <HomeContext.Provider
      value={{
        getHomeData,
        getBabyWeeklyGrowth,
        respGetBabyWeeklyGrowth,
        getDetailWeek,
        updateGrief,
        getDataTimeline,
        getSubscriptionsCorporate,
        postSubscriptionsCorporate
      }}
    >
      {children}
    </HomeContext.Provider>
  )
}

export default HomeProvider
